import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, ArrowLeft } from 'lucide-react';

export const HowIWork = () => {
  const stats = [
    { value: '$50M+', label: 'Total ad spend governed' },
    { value: '120+', label: 'Conversion Experiments Run' },
    { value: '6+ years', label: 'Across regulated and DTC markets' },
    { value: '3 verticals', label: 'DTC, iGaming, SaaS' }
  ];

  const methodology = [
    {
      number: '01',
      title: 'Audit the full funnel',
      detail: 'Before proposing a single test, I map your entire conversion architecture - from ad click to completed transaction. I use session recordings, heatmaps, Meta Ad Library, competitor analysis, and checkout flow audits to find the 2-3 highest-impact leaks. Most funnels have 5-10 problems. Only 2-3 are worth fixing.',
      tools: ['SimilarWeb', 'Meta Ad Library', 'Hotjar', 'Mouseflow', 'GTM', 'Looker Studio'],
      result: 'TukarBateri audit identified WhatsApp CTA as the key lever - resulted in +30% CTR in 7 days'
    },
    {
      number: '02',
      title: 'Prioritise by revenue impact, not ease',
      detail: 'I stack-rank every potential experiment by estimated revenue lift. Easy wins that move the needle by 2% get killed. Hard tests that could move it by 15% get prioritised. This is where most CRO programmes fail - they optimise for test velocity instead of revenue velocity.',
      tools: ['Airtable', 'Revenue modeling (internal)', 'Looker Studio'],
      result: 'Puffy: Prioritisation framework led to 5% blended CVR lift = $20M ARR, vs chasing small wins'
    },
    {
      number: '03',
      title: 'Run tests fast, read results faster',
      detail: '7-14 day test cycles. I code tests directly in HTML/CSS/JS to remove IT as a dependency. Results go into a shared Airtable so the whole team learns - not just me. Fast iteration beats perfect execution.',
      tools: ['Convert', 'VWO', 'Growthbook', 'Airtable', 'HTML/CSS/JS'],
      result: 'iGaming client: Test velocity increased, enabling creative-to-CRO feedback loop that scaled deposits 6.7x'
    },
    {
      number: '04',
      title: 'Integrate CRO with paid media strategy',
      detail: 'CRO and paid media should never operate in silos. I build feedback loops where ad-level performance data informs landing page test priorities, and CRO learnings feed directly back into creative briefs and audience segmentation. This is how you scale spend without destroying efficiency.',
      tools: ['Meta Ads Manager', 'Google Ads', 'Convert', 'Airtable', 'Miro'],
      result: 'iGaming platform: Automated creative-to-CRO loop eliminated lag, daily deposits scaled from $30k to $200k'
    },
    {
      number: '05',
      title: 'Own the revenue outcome',
      detail: 'I don\'t hand off a report and disappear. I own the number - from traffic source to completed transaction. If the revenue doesn\'t move, I go back to the audit. If it does, I document what worked and why so the team can replicate it.',
      tools: ['Looker Studio', 'Airtable', 'GTM', 'Revenue dashboards'],
      result: 'TukarBateri: Revenue scaled from $30k to $100k/month in 5 months with zero increase in traffic'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#F5F5F5]">
      <section className="container mx-auto px-6 py-20 max-w-6xl">
        <h1 className="text-5xl font-bold mb-16">How I work</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <Card key={index} className="bg-[#1a1a1a] border-none p-6 space-y-2">
              <div className="text-3xl font-bold text-[#E85D04]">{stat.value}</div>
              <div className="text-[#888888] text-sm leading-relaxed">{stat.label}</div>
            </Card>
          ))}
        </div>

        {/* Feedback Loop Diagram */}
        <div className="mb-16 bg-[#1a1a1a] p-12 rounded-lg">
          <h2 className="text-2xl font-semibold mb-8 text-center">The feedback loop</h2>
          <div className="flex flex-col items-center">
            {/* Diagram */}
            <div className="relative w-full max-w-3xl">
              {/* Nodes */}
              <div className="flex justify-between items-center mb-8">
                <div className="flex-1 text-center">
                  <div className="inline-block bg-[#E85D04] text-white px-6 py-3 rounded-lg font-semibold">
                    Creative strategy
                  </div>
                </div>
                <div className="text-[#E85D04] text-2xl px-4">→</div>
                <div className="flex-1 text-center">
                  <div className="inline-block bg-[#E85D04] text-white px-6 py-3 rounded-lg font-semibold">
                    Paid media
                  </div>
                </div>
                <div className="text-[#E85D04] text-2xl px-4">→</div>
                <div className="flex-1 text-center">
                  <div className="inline-block bg-[#E85D04] text-white px-6 py-3 rounded-lg font-semibold">
                    CRO
                  </div>
                </div>
                <div className="text-[#E85D04] text-2xl px-4">→</div>
                <div className="flex-1 text-center">
                  <div className="inline-block bg-[#E85D04] text-white px-6 py-3 rounded-lg font-semibold">
                    Revenue
                  </div>
                </div>
              </div>

              {/* Feedback Arrow */}
              <div className="flex items-center justify-center mt-8">
                <div className="w-full relative">
                  {/* Dashed line */}
                  <div className="w-full border-t-2 border-dashed border-[#E85D04]"></div>
                  
                  {/* Arrow pointing left (feedback direction) */}
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1">
                    <ArrowLeft className="text-[#E85D04] w-8 h-8" strokeWidth={3} />
                  </div>
                  
                  {/* Label */}
                  <div className="absolute left-1/2 top-6 transform -translate-x-1/2 text-[#888888] text-sm italic whitespace-nowrap">
                    CRO learnings feed back into creative briefs
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Methodology Steps */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold mb-8">The methodology</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {methodology.map((step) => (
              <AccordionItem 
                key={step.number} 
                value={step.number}
                className="bg-[#1a1a1a] border-none rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-[#1a1a1a]/80 data-[state=open]:bg-[#0a0a0a] transition-colors">
                  <div className="flex items-center gap-6 text-left w-full">
                    <span className="text-4xl font-bold text-[#E85D04] min-w-[60px]">
                      {step.number}
                    </span>
                    <span className="text-2xl font-semibold text-[#F5F5F5]">
                      {step.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-8 pt-4 bg-[#0a0a0a]">
                  <div className="space-y-6 pl-[76px]">
                    {/* Detail */}
                    <p className="text-[#888888] leading-relaxed text-lg">
                      {step.detail}
                    </p>

                    {/* Tools Used */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#F5F5F5] mb-3">Tools used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {step.tools.map((tool) => (
                          <Badge 
                            key={tool} 
                            variant="outline" 
                            className="bg-[#1a1a1a] border-[#888888] text-[#888888] px-3 py-1"
                          >
                            {tool}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Real Result */}
                    <div className="border-l-4 border-l-[#22c55e] pl-6 py-3 bg-[#1a1a1a]">
                      <h4 className="text-sm font-semibold text-[#F5F5F5] mb-2">Real result:</h4>
                      <p className="text-[#888888]">{step.result}</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-20">
          <Link to="/case-studies">
            <Button className="bg-[#E85D04] hover:bg-[#E85D04]/90 text-white px-8 py-6 text-lg font-semibold transition-colors group">
              See the results
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
